import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import './SystemStatusPage.css';

const STATUS_LABELS = {
  ok: 'Correcto',
  configured: 'Configurado',
  warning: 'Atención',
  error: 'Error',
  disabled: 'Desactivado',
};

function StatusBadge({ status }) {
  return (
    <span className={`system-status-badge is-${status || 'disabled'}`}>
      <span className="system-status-dot" aria-hidden="true" />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function formatDate(value) {
  if (!value) return 'Sin registro';
  return new Intl.DateTimeFormat('es-UY', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date(value));
}

export default function SystemStatusPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/system/status');
      setStatus(data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo consultar el estado del sistema');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  return (
    <div className="system-status-page">
      <div className="page-header system-status-header">
        <div>
          <h1 className="page-title">Estado del sistema</h1>
          <p className="page-subtitle">Servicios, integraciones e integridad de datos.</p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={loadStatus} disabled={loading}>
          {loading ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!status && loading ? (
        <div className="system-status-loading">
          <div className="spinner" /> Consultando servicios...
        </div>
      ) : status && (
        <>
          <section className="system-status-summary" aria-label="Resumen del sistema">
            <div>
              <span className="system-status-eyebrow">Estado general</span>
              <div className="system-status-summary-line">
                <StatusBadge status={status.overall} />
                <span>Actualizado {formatDate(status.generated_at)}</span>
              </div>
            </div>
            <dl className="system-status-facts">
              <div>
                <dt>Entorno</dt>
                <dd>{status.application.environment}</dd>
              </div>
              <div>
                <dt>Base</dt>
                <dd>{status.database.latency_ms} ms</dd>
              </div>
              <div>
                <dt>Migraciones</dt>
                <dd>{status.database.migrations_applied}</dd>
              </div>
              <div>
                <dt>Uptime</dt>
                <dd>{Math.floor(status.application.uptime_seconds / 60)} min</dd>
              </div>
            </dl>
          </section>

          <div className="system-status-columns">
            <section className="system-status-panel">
              <header>
                <div>
                  <span className="system-status-eyebrow">Conectividad</span>
                  <h2>Servicios e integraciones</h2>
                </div>
              </header>
              <div className="system-status-list">
                <div className="system-status-row">
                  <div>
                    <strong>Backend</strong>
                    <span>Node {status.application.node_version}</span>
                  </div>
                  <StatusBadge status={status.application.status} />
                </div>
                <div className="system-status-row">
                  <div>
                    <strong>PostgreSQL</strong>
                    <span>Hora del servidor {formatDate(status.database.server_time)}</span>
                  </div>
                  <StatusBadge status={status.database.status} />
                </div>
                {status.integrations.map(integration => (
                  <div className="system-status-row" key={integration.key}>
                    <div>
                      <strong>{integration.label}</strong>
                      {integration.detail && <span>{integration.detail}</span>}
                    </div>
                    <StatusBadge status={integration.status} />
                  </div>
                ))}
              </div>
            </section>

            <section className="system-status-panel">
              <header>
                <div>
                  <span className="system-status-eyebrow">Base operativa</span>
                  <h2>Integridad de datos</h2>
                </div>
                <div className="system-status-counts">
                  <span>{status.data.active_personnel} personas</span>
                  <span>{status.data.active_users} accesos</span>
                </div>
              </header>
              <div className="system-status-list">
                {status.data.checks.map(check => (
                  <div className="system-status-row" key={check.key}>
                    <div>
                      <strong>{check.label}</strong>
                      <span>{check.value === 0 ? 'Sin observaciones' : `${check.value} registro${check.value === 1 ? '' : 's'}`}</span>
                    </div>
                    <StatusBadge status={check.status} />
                  </div>
                ))}
              </div>
            </section>
          </div>

          <p className="system-status-footnote">
            Esta vista es de solo lectura. Informa presencia de configuración y resultados agregados; no expone credenciales ni datos personales.
          </p>
        </>
      )}
    </div>
  );
}
